/* global qualified */
/**
 * Global CTA Form Boilerplate
 *
 * Can use global content from the Footer Data options page
 * or be customized per-page with a theme and content.
 */

import get from "lodash.get";
import { createComponent, FelaComponent } from "react-fela";
import { combineRules } from "fela";
import { gutters, layout } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import { textStyles, themeText, themeHeadline } from "~/styleguide/typography";
import globalData from "~/data/site-data.json";

import { useContext, useEffect, useRef } from "react";
import { getThemeForModule } from "~/context/theme";
import { DataContext } from "~/context/content";
import { ContentMaxWidth } from "~/components/layout/max-width";

let ctaFormInstanceCount = 0;

export default function CTAForm({ block }) {
  let {
    anchor_id,
    use_global_content,
    theme_colors,
    background_image,
    heading,
    copy,
    form,
    qexperience,
  } = block;

  const pageData = useContext(DataContext);
  const page = get(pageData, "listing_page", pageData);

  if (use_global_content) {
    const contentObject = use_global_content ? globalData : page;
    const keyPrefix = use_global_content ? "data." : "";

    theme_colors = get(
      contentObject,
      `${keyPrefix}acf.cta_boilerplate_theme_colors`
    );
    background_image = get(
      contentObject,
      `${keyPrefix}acf.cta_boilerplate_image`
    );
    heading = get(contentObject, `${keyPrefix}acf.cta_boilerplate_heading`);
    copy = get(contentObject, `${keyPrefix}acf.cta_boilerplate_copy`);
    form = get(
      contentObject,
      `${keyPrefix}acf.cta_boilerplate_hubspot_form_id`
    );
    qexperience = get(
      contentObject,
      `${keyPrefix}acf.cta_boilerplate_qexperience`
    );
  }

  const instanceId = useRef(`hubspot-${form}-${++ctaFormInstanceCount}`);

  const imageUrl =
    typeof background_image === "string"
      ? background_image
      : background_image && background_image.url;
  const theme = getThemeForModule(theme_colors);
  const textTheme = themeText(theme);
  const headlineTheme = themeHeadline(theme);

  if (!form) return null;

  // inject HubSpot script once
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://js.hsforms.net/forms/embed/developer/5265893.js";
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Qualified tracking on form submit
  useEffect(() => {
    if (!qexperience) return;
    const handleQualifiedSubmit = (e) => {
      const submittedForm = e.target;
      if (
        submittedForm.matches &&
        submittedForm.matches(`form[data-form-id='${form}']`)
      ) {
        const formData = new FormData(submittedForm);
        qualified("saveFormData", {
          email: formData.get("0-1/email"),
          salesforce_crm: formData.get("0-1/do_you_use_salesforce_crm_"),
          size_of_sales_team: formData.get("0-1/number_of_sales_reps__c"),
        });
        qualified("emitFormFill", "custom");
        console.log("emit form fill");
      }
    };
    const handleQExperience = () => {
      qualified("showFormExperience", `experience-${qexperience}`);
      console.log("show experience");
    };
    window.addEventListener("submit", handleQualifiedSubmit);
    window.addEventListener(
      "hs-form-event:on-submission:success",
      handleQExperience
    );
    return () => {
      window.removeEventListener("submit", handleQualifiedSubmit);
      window.removeEventListener(
        "hs-form-event:on-submission:success",
        handleQExperience
      );
    };
  }, [qexperience, form]);

  const backgroundStyles = imageUrl
    ? {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundColor: theme.primary,
      }
    : {
        background: theme.gradient || theme.primary,
      };

  return (
    <Container
      extend={backgroundStyles}
      id={anchor_id}
      className="module-cta-form"
    >
      <ContentMaxWidth extend={colWrapStyles({ textSide: "left" })}>
        <Col textSide="left">
          <FelaComponent style={{ maxWidth: 600 }}>
            {heading && (
              <FelaComponent
                style={combineRules(
                  ctaFormHeadingStyle(!!copy),
                  headlineTheme,
                  textStyles.heading2
                )}
              >
                <span dangerouslySetInnerHTML={{ __html: heading }} />
              </FelaComponent>
            )}
            {copy && (
              <CTAFormText
                as="p"
                extend={combineRules(textTheme, textStyles.paragraphLarge)}
                dangerouslySetInnerHTML={{ __html: copy }}
                passThrough={["dangerouslySetInnerHTML"]}
              />
            )}
          </FelaComponent>
        </Col>
        <Col textSide="left">
          <div
            id={instanceId.current}
            style={FormStyle}
            className="hs-form-html"
            data-region="na1"
            data-portal-id="5265893"
            data-form-id={form}
          />
        </Col>
      </ContentMaxWidth>
    </Container>
  );
}

// ========== STYLES ==========

const Container = createComponent(({ extend: e = {} }) => ({
  paddingTop: 87,
  paddingBottom: 87,
  extend: [fluid("paddingH", gutters.default), e],
  minWidthMobile: {
    extend: [fluid("paddingV", gutters.vertical)],
  },
}));

const colWrapStyles = ({ textSide }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "stretch",
  maxWidth: 1300,
  position: "relative",
  maxWidthMobile: {
    flexWrap: "wrap",
  },
  extend: [
    {
      condition: textSide === "right",
      style: {
        flexDirection: "row-reverse",
      },
    },
  ],
});

const colMargin = (direction) =>
  fluid(direction, {
    min: 20,
    max: 40,
    maxWidth: layout.contentMaxWidth,
  });

const Col = createComponent(({ textSide }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  alignContent: "center",
  flexWrap: "wrap",
  ":first-of-type": {
    width: "55%",
  },
  ":last-of-type": {
    width: "45%",
  },
  maxWidthMobile: {
    width: "100%!important",
    textAlign: "center",
    ":first-of-type": {
      marginBottom: 44,
    },
  },
  extend: [
    {
      condition: textSide === "left",
      style: {
        minWidthTablet: {
          ":first-of-type": {
            extend: [colMargin("marginRight")],
          },
        },
        minWidthMobile: {
          ":last-of-type": {
            extend: [colMargin("marginLeft")],
            alignItems: "stretch",
          },
        },
      },
    },
  ],
}));

const CTAFormText = createComponent(
  ({ subheading }) => ({
    extend: [
      {
        condition: subheading,
        style: {
          marginBottom: 30,
        },
      },
    ],
  }),
  "div",
  ["dangerouslySetInnerHTML"]
);

const FormStyle = {
  width: "100%",
};

const ctaFormHeadingStyle = (hasCopy) => ({
  extend: [
    hasCopy ? fluid("marginBottom", { min: 20, max: 50 }) : { marginBottom: 0 },
  ],
});
